import * as Colors from '@brightlayer-ui/colors';
import { AddCircle, Check, Person, Warning } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PointOfContactFooter } from '../../components/PointOfContactFooter';
import { useAlert } from '../../lib/AlertContext';
import { Logger } from '../../logger';
import { InstallerInvitationService } from '../../services/installerInvitation/installerInvitation.service';
import { InstallerInvitationWithStatus } from '../../services/installerInvitation/types';
import { OrganizationService } from '../../services/organization/organization.service';
import { Organization } from '../../services/organization/types';
import { OrganizationId } from '../../types/idFlavors';
import { InstallerInvitationsSection } from './InstallerInvitationsSection';
import { InviteInstallerModal } from './InviteInstallerModal';

const styles = {
    contentBody: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 'calc(100vh - 56px)',
        padding: '32px',
        backgroundColor: Colors.white[200],
    },
    card: {
        backgroundColor: Colors.white[50],
        borderRadius: '2px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        flex: 1,
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
};

type ManageInstallersProps = {
    organization?: Organization;
};

export const ManageInstallers: React.FC<ManageInstallersProps> = ({ organization: passedOrganization }) => {
    const { t } = useTranslation();
    const { showInfoAlertToUser, showConfirmationToUser, showErrorToUser } = useAlert();
    const params = useParams();
    const orgId = params.id as OrganizationId | undefined;
    const [organization, setOrganization] = useState(passedOrganization);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [invitations, setInvitations] = useState<InstallerInvitationWithStatus[]>([]);
    const [invitationsLoading, setInvitationsLoading] = useState(true);
    const [invitationsError, setInvitationsError] = useState(false);

    // Reset scroll position on mount to prevent the page from appearing scrolled down.
    // This page shows a loading spinner before the invitation data is fetched, which causes
    // a content height shift when the table renders — without this, the browser may retain
    // stale scroll position from a previously visited page.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const fetchInvitations = useCallback(async (): Promise<void> => {
        if (!orgId) return;
        try {
            setInvitationsLoading(true);
            const data = await InstallerInvitationService.getInstallerInvitations(orgId);
            setInvitations(Array.isArray(data) ? data : []);
            setInvitationsError(false);
        } catch {
            setInvitationsError(true);
        } finally {
            setInvitationsLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        if (orgId) {
            void fetchInvitations();
        }
    }, [orgId, fetchInvitations]);

    useEffect(() => {
        const fetchOrganization = async (): Promise<void> => {
            if (!orgId) return;
            try {
                const org = await OrganizationService.getOrganization(orgId);
                setOrganization(org);
            } catch (error) {
                Logger.error(String(error));
            }
        };
        if (orgId && !passedOrganization) {
            void fetchOrganization();
        }
    }, [orgId, passedOrganization]);

    const organizationName = organization?.name ?? '';

    const handleResendInvitation = async (email: string): Promise<void> => {
        if (!orgId) {
            Logger.error('Cannot resend invitation: organizationId is missing');
            return;
        }
        try {
            await InstallerInvitationService.createInstallerInvitation({
                organizationId: orgId,
                email,
                overrideInvitation: true,
            });
            showInfoAlertToUser({
                title: t('MANAGE_INSTALLERS_PAGE.INVITATION_RESENT_TITLE'),
                description: t('MANAGE_INSTALLERS_PAGE.INVITATION_RESENT_DESCRIPTION'),
                icon: <Check sx={{ fontSize: 38, color: Colors.blue[500] }} />,
                iconBGColor: Colors.blue[50],
                onDismiss: () => void fetchInvitations(),
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            Logger.error(
                `Failed to resend installer invitation for org ${orgId} (${email.slice(0, 3)}***): ${message}`
            );
            showErrorToUser({
                errorTitle: t('MANAGE_INSTALLERS_PAGE.FAILED_TO_RESEND_INVITATION_TITLE'),
                errorDescription: t('MANAGE_INSTALLERS_PAGE.FAILED_TO_RESEND_INVITATION_DESCRIPTION'),
                confirmText: t('COMMON.OK'),
                icon: <Warning sx={{ fontSize: 38, color: Colors.orange[500] }} />,
                iconBGColor: Colors.orange[50],
            });
        }
    };

    const handleInviteInstaller = async (email: string): Promise<void> => {
        if (!orgId) {
            Logger.error('Cannot invite installer: organizationId is missing');
            return;
        }
        setInviteLoading(true);
        try {
            await InstallerInvitationService.createInstallerInvitation({
                organizationId: orgId,
                email,
            });
            setShowInviteModal(false);
            showInfoAlertToUser({
                title: t('MANAGE_INSTALLERS_PAGE.REQUEST_SENT_TITLE'),
                description: t('MANAGE_INSTALLERS_PAGE.REQUEST_SENT_DESCRIPTION'),
                icon: <Check sx={{ fontSize: 38, color: Colors.blue[500] }} />,
                iconBGColor: Colors.blue[50],
                onDismiss: () => void fetchInvitations(),
            });
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
                setShowInviteModal(false);
                showInfoAlertToUser({
                    title: t('MANAGE_INSTALLERS_PAGE.EXISTING_INSTALLER_DIALOG_TITLE'),
                    description: t('MANAGE_INSTALLERS_PAGE.EXISTING_INSTALLER_DIALOG_BODY'),
                    icon: <Person sx={{ fontSize: 38, color: Colors.blue[500] }} />,
                    iconBGColor: Colors.blue[50],
                });
            } else if (isAxiosError(error) && error.response?.status === 400) {
                setShowInviteModal(false);
                showConfirmationToUser({
                    title: t('MANAGE_INSTALLERS_PAGE.DUPLICATE_INVITATION_MODAL_TITLE'),
                    description: t('MANAGE_INSTALLERS_PAGE.DUPLICATE_INVITATION_MODAL_BODY'),
                    icon: <Person sx={{ fontSize: 38, color: Colors.blue[500] }} />,
                    iconBGColor: Colors.blue[50],
                    confirmText: t('MANAGE_INSTALLERS_PAGE.RESEND_INVITATION_BUTTON'),
                    cancelText: t('COMMON.CANCEL'),
                    onConfirm: () => handleResendInvitation(email),
                    testId: 'duplicate-invitation-modal',
                });
            } else {
                const message = error instanceof Error ? error.message : String(error);
                Logger.error(
                    `Failed to create installer invitation for org ${orgId} (${email.slice(0, 3)}***): ${message}`
                );
                setShowInviteModal(false);
                showErrorToUser({
                    errorTitle: t('MANAGE_INSTALLERS_PAGE.FAILED_TO_CREATE_INVITATION_TITLE'),
                    errorDescription: t('MANAGE_INSTALLERS_PAGE.FAILED_TO_CREATE_INVITATION_DESCRIPTION'),
                    confirmText: t('COMMON.OK'),
                    icon: <Warning sx={{ fontSize: 38, color: Colors.orange[500] }} />,
                    iconBGColor: Colors.orange[50],
                });
            }
        } finally {
            setInviteLoading(false);
        }
    };

    return (
        <>
            <Box sx={styles.contentBody}>
                <Box sx={styles.card}>
                    <Box sx={styles.headerRow}>
                        <Box>
                            <Typography variant="h6" fontWeight={600} color={Colors.gray[800]}>
                                {t('MANAGE_INSTALLERS_PAGE.TITLE')}
                            </Typography>
                            <Typography variant="body2" color={Colors.gray[500]}>
                                {t('MANAGE_INSTALLERS_PAGE.DESCRIPTION', { organizationName })}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Installers title + Invite button row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography
                            sx={{ fontSize: '20px', fontWeight: 600, color: Colors.gray[800] }}
                            variant="h6"
                            data-testid="installers-section-title"
                        >
                            {t('MANAGE_INSTALLERS_PAGE.INSTALLERS_SECTION_TITLE')}
                        </Typography>
                        <Button
                            startIcon={<AddCircle sx={{ fontSize: '16px' }} />}
                            variant="contained"
                            disableElevation
                            data-testid="invite-installer-button"
                            onClick={(): void => setShowInviteModal(true)}
                            sx={{
                                backgroundColor: Colors.blue[500],
                                color: Colors.white[50],
                                fontSize: '14px',
                                fontWeight: 600,
                                height: '36px',
                                minWidth: '150px',
                                borderRadius: '4px',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: Colors.blue[800] },
                            }}
                        >
                            {t('MANAGE_INSTALLERS_PAGE.INVITE_INSTALLER_BUTTON')}
                        </Button>
                    </Box>

                    <InstallerInvitationsSection
                        invitations={invitations}
                        loading={invitationsLoading}
                        error={invitationsError}
                    />
                </Box>
            </Box>
            {organization && <PointOfContactFooter pointOfContact={organization.pointOfContact} />}
            {showInviteModal && (
                <InviteInstallerModal
                    handleInviteInstaller={handleInviteInstaller}
                    onClose={(): void => setShowInviteModal(false)}
                    loading={inviteLoading}
                />
            )}
        </>
    );
};
